import { useState, useEffect } from "react";
import axios from "axios";
import { 
  MessageSquare, Trash2, Eye, Loader2, ChevronLeft, ChevronRight,
  User, Bot, Calendar, Hash, AlertTriangle, CheckCircle, XCircle,
  Copy, Check, Archive, ArchiveRestore, RefreshCw, Mic, Play, Pause, FileText
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lead Status Labels und Farben
const LEAD_STATUS_CONFIG = {
  new: { label: "Neu", color: "bg-green-100 text-green-700" },
  waitlist: { label: "Nicht erreicht", color: "bg-amber-100 text-amber-700" },
  contacted: { label: "Kann erst später", color: "bg-yellow-100 text-yellow-700" },
  qualified: { label: "Qualifiziert", color: "bg-blue-100 text-blue-700" },
  unqualified: { label: "Ungeeignet", color: "bg-red-100 text-red-700" },
  contract_sent: { label: "Weitergeleitet an T2M", color: "bg-purple-100 text-purple-700" },
  hired: { label: "Eingestellt", color: "bg-emerald-100 text-emerald-700" },
  duplicate: { label: "Doppelt", color: "bg-orange-100 text-orange-700" },
  abandoned: { label: "Bedarf noch Klärung", color: "bg-amber-100 text-amber-700" },
  appointment_scheduled: { label: "Termin vereinbart", color: "bg-indigo-100 text-indigo-700" },
  interview_done: { label: "Interview erledigt", color: "bg-teal-100 text-teal-700" },
  rejected: { label: "Abgelehnt", color: "bg-gray-100 text-gray-700" }
};

export const AdminChatSessions = ({ authToken, userRole, projectId }) => {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedSession, setSelectedSession] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteCount, setBulkDeleteCount] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [copiedTranscript, setCopiedTranscript] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [bulkRestoreDialogOpen, setBulkRestoreDialogOpen] = useState(false);
  const [bulkRestoreCount, setBulkRestoreCount] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isPlayingVoice, setIsPlayingVoice] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${authToken}` }
  });

  useEffect(() => {
    fetchSessions();
  }, [page, showArchived, projectId]);

  const fetchSessions = async () => {
    setIsLoading(true);
    try {
      let url = `${API}/admin/chat-sessions?page=${page}&limit=25&show_archived=${showArchived}`;
      if (projectId) url += `&project_id=${projectId}`;
      const response = await axios.get(url, getAuthHeaders());
      setSessions(response.data.sessions);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error("Fehler beim Laden der Chat-Sessions");
    } finally {
      setIsLoading(false);
    }
  };

  const handleUnarchive = async (sessionIds) => {
    try {
      await axios.post(`${API}/admin/chat-sessions/bulk-archive`, {
        session_ids: Array.isArray(sessionIds) ? sessionIds : [sessionIds],
        archive: false
      }, getAuthHeaders());
      toast.success("Session(s) wiederhergestellt");
      setSelectedIds(new Set());
      fetchSessions();
    } catch (error) {
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  // Selection helpers
  const toggleSelect = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  };

  const selectAll = () => {
    setSelectedIds(new Set(sessions.map(s => s.id)));
  };

  const selectNone = () => {
    setSelectedIds(new Set());
  };

  const selectCount = async (count) => {
    // Fetch oldest N sessions and select them
    try {
      const response = await axios.get(
        `${API}/admin/chat-sessions?limit=${count}&sort_by=created_at&sort_order=asc&show_archived=${showArchived}`, 
        getAuthHeaders()
      );
      const ids = response.data.sessions.map(s => s.id);
      setSelectedIds(new Set(ids));
      toast.success(`${ids.length} älteste Sessions markiert`);
    } catch (error) {
      toast.error("Fehler beim Markieren");
    }
  };

  // Bulk actions on selected
  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await axios.post(`${API}/admin/chats/bulk-delete`, {
        session_ids: Array.from(selectedIds)
      }, getAuthHeaders());
      toast.success(`${selectedIds.size} Sessions gelöscht`);
      setSelectedIds(new Set());
      fetchSessions();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const archiveSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await axios.post(`${API}/admin/chat-sessions/bulk-archive`, {
        session_ids: Array.from(selectedIds),
        archive: true
      }, getAuthHeaders());
      toast.success(`${selectedIds.size} Sessions archiviert`);
      setSelectedIds(new Set());
      fetchSessions();
    } catch (error) {
      toast.error("Fehler beim Archivieren");
    }
  };

  const restoreSelected = async () => {
    if (selectedIds.size === 0) return;
    try {
      await axios.post(`${API}/admin/chat-sessions/bulk-archive`, {
        session_ids: Array.from(selectedIds),
        archive: false
      }, getAuthHeaders());
      toast.success(`${selectedIds.size} Sessions wiederhergestellt`);
      setSelectedIds(new Set());
      fetchSessions();
    } catch (error) {
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  const viewSession = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/admin/chat-sessions/${sessionId}`, getAuthHeaders());
      setSelectedSession(response.data);
      setViewDialogOpen(true);
      setCopiedTranscript(false);
      setIsPlayingVoice(false);
      
      // Fetch voice message if exists
      try {
        const voiceResponse = await axios.get(`${API}/voice/by-session/${sessionId}`, getAuthHeaders());
        if (voiceResponse.data.found) {
          setVoiceMessage(voiceResponse.data);
        } else {
          setVoiceMessage(null);
        }
      } catch (err) {
        setVoiceMessage(null);
      }
    } catch (error) {
      toast.error("Fehler beim Laden der Session");
    }
  };

  const copyTranscriptToClipboard = () => {
    // Support both transcript_json and messages formats
    const messages = selectedSession?.session?.transcript_json || selectedSession?.session?.messages || [];
    if (!messages.length) return;
    
    const leadName = selectedSession.lead?.first_name || 'Bewerber';
    const header = `Chat-Verlauf für ${leadName}\n`;
    const separator = "─".repeat(40) + "\n";
    
    const formattedMessages = messages.map(msg => {
      const sender = msg.role === 'user' ? '👤 Bewerber' : '🤖 Bot';
      return `${sender}:\n${msg.content}`;
    }).join('\n\n');
    
    const fullText = header + separator + formattedMessages;
    
    navigator.clipboard.writeText(fullText);
    setCopiedTranscript(true);
    toast.success("Chat-Verlauf kopiert!");
    setTimeout(() => setCopiedTranscript(false), 2000);
  };

  const confirmDelete = (session) => {
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const deleteSession = async () => {
    if (!sessionToDelete) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/admin/chat-sessions/${sessionToDelete.session_id}`, getAuthHeaders());
      toast.success("Chat-Session gelöscht");
      fetchSessions();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    }
  };

  const confirmBulkDelete = (count) => {
    setBulkDeleteCount(count);
    setBulkDeleteDialogOpen(true);
  };

  const bulkDelete = async () => {
    setIsDeleting(true);
    try {
      let params;
      if (bulkDeleteCount === "all") {
        params = { count: -1, filter_type: "all" };
      } else if (bulkDeleteCount === "empty") {
        params = { count: -1, filter_type: "empty" };
      } else {
        // Delete oldest N - Backend handles sorting correctly
        params = { count: parseInt(bulkDeleteCount), filter_type: "oldest" };
      }
      
      const response = await axios.post(`${API}/admin/chats/bulk-delete`, params, getAuthHeaders());
      toast.success(`${response.data.deleted} Sessions gelöscht`);
      fetchSessions();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    } finally {
      setIsDeleting(false);
      setBulkDeleteDialogOpen(false);
      setBulkDeleteCount(null);
    }
  };

  const handleBulkArchive = async (count) => {
    try {
      let requestData;
      if (count === -1) {
        // Archive all
        requestData = { all_sessions: true, archive: true };
      } else if (count === "empty") {
        // Archive empty sessions - get them first then archive
        const emptyResponse = await axios.get(`${API}/admin/chat-sessions?limit=1000`, getAuthHeaders());
        const emptySessions = emptyResponse.data.sessions.filter(s => s.message_count === 0);
        const sessionIds = emptySessions.map(s => s.id);
        requestData = { session_ids: sessionIds, archive: true };
      } else {
        // Archive oldest N sessions - sort by created_at ascending to get oldest first
        const response = await axios.get(`${API}/admin/chat-sessions?limit=${count}&sort_by=created_at&sort_order=asc`, getAuthHeaders());
        const sessionIds = response.data.sessions.map(s => s.id);
        requestData = { session_ids: sessionIds, archive: true };
      }
      
      const response = await axios.post(`${API}/admin/chat-sessions/bulk-archive`, requestData, getAuthHeaders());
      toast.success(`${response.data.updated} Sessions archiviert`);
      fetchSessions();
    } catch (error) {
      console.error("Archive error:", error);
      toast.error("Fehler beim Archivieren");
    }
  };

  // Wiederherstellen von archivierten Sessions
  const handleBulkRestore = async (count) => {
    try {
      let requestData;
      if (count === -1) {
        // Restore all archived
        requestData = { all_sessions: true, archive: false };
      } else {
        // Restore oldest N archived sessions
        const response = await axios.get(`${API}/admin/chat-sessions?limit=${count}&sort_by=created_at&sort_order=asc&show_archived=true`, getAuthHeaders());
        const sessionIds = response.data.sessions.map(s => s.id);
        requestData = { session_ids: sessionIds, archive: false };
      }
      
      const response = await axios.post(`${API}/admin/chat-sessions/bulk-archive`, requestData, getAuthHeaders());
      toast.success(`${response.data.updated} Sessions wiederhergestellt`);
      fetchSessions();
      setBulkRestoreDialogOpen(false);
      setBulkRestoreCount(null);
    } catch (error) {
      console.error("Restore error:", error);
      toast.error("Fehler beim Wiederherstellen");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const getStatusBadge = (session) => {
    // Wenn Lead existiert, zeige immer "Neu" (ursprünglicher Status bei Erstellung)
    if (session.lead_info) {
      return <Badge className="bg-green-100 text-green-700">Neu</Badge>;
    }
    // Wenn Lead gelöscht wurde
    if (session.lead_deleted) {
      return <Badge className="bg-red-100 text-red-700">Lead gelöscht</Badge>;
    }
    // Kein Lead erstellt - zeige Chat-Zustand
    if (session.message_count <= 2) {
      return <Badge className="bg-slate-100 text-slate-600">Nur gestartet</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700">Kein Lead</Badge>;
  };

  if (userRole !== "admin") {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Nur Admins können Chat-Sessions einsehen.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="chat-sessions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-blue-600" />
            {showArchived ? "Archivierte Chat-Sessions" : "Alle Chat-Sessions"}
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            {total} {showArchived ? "archivierte" : ""} Chat-Sessions
          </p>
        </div>
        
        {/* Archive Toggle + Bulk Actions */}
        <div className="flex items-center gap-4">
          {/* Archive Toggle */}
          <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
            <Archive className={`w-4 h-4 ${showArchived ? 'text-amber-600' : 'text-slate-400'}`} />
            <Label htmlFor="show-archived" className="text-sm cursor-pointer">
              Archiv anzeigen
            </Label>
            <Switch
              id="show-archived"
              checked={showArchived}
              onCheckedChange={(checked) => {
                setShowArchived(checked);
                setPage(1);
                setSelectedIds(new Set());
              }}
            />
          </div>
          
          {/* Refresh Button */}
          <Button 
            variant="outline" 
            size="icon"
            onClick={fetchSessions}
            className="rounded-lg"
            title="Aktualisieren"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Selection Bar */}
      <div className="flex items-center gap-2 flex-wrap bg-slate-50 p-3 rounded-lg border">
        <span className="text-sm font-medium text-slate-700 mr-2">
          Auswahl: {selectedIds.size} markiert
        </span>
        
        {/* Quick Select */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Check className="w-4 h-4 mr-1" />
              Markieren
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => selectCount(10)}>Älteste 10</DropdownMenuItem>
            <DropdownMenuItem onClick={() => selectCount(20)}>Älteste 20</DropdownMenuItem>
            <DropdownMenuItem onClick={() => selectCount(50)}>Älteste 50</DropdownMenuItem>
            <DropdownMenuItem onClick={() => selectCount(100)}>Älteste 100</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={selectAll}>Alle auf Seite</DropdownMenuItem>
            <DropdownMenuItem onClick={selectNone}>Keine</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Actions on selected */}
        {selectedIds.size > 0 && (
          <>
            <Button 
              variant="outline" 
              size="sm" 
              className="text-red-600 border-red-200 hover:bg-red-50"
              onClick={() => {
                if (window.confirm(`${selectedIds.size} Sessions wirklich löschen?`)) {
                  deleteSelected();
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Löschen ({selectedIds.size})
            </Button>
            
            {!showArchived ? (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                onClick={archiveSelected}
              >
                <Archive className="w-4 h-4 mr-1" />
                Archivieren ({selectedIds.size})
              </Button>
            ) : (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-green-600 border-green-200 hover:bg-green-50"
                onClick={restoreSelected}
              >
                <ArchiveRestore className="w-4 h-4 mr-1" />
                Wiederherstellen ({selectedIds.size})
              </Button>
            )}
          </>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-20">
              <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Keine Chat-Sessions vorhanden</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input 
                      type="checkbox"
                      checked={selectedIds.size === sessions.length && sessions.length > 0}
                      onChange={(e) => e.target.checked ? selectAll() : selectNone()}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                  </TableHead>
                  <TableHead>Session ID</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Nachrichten</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Vorschau</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => {
                  const sessionId = session.session_id || session.id;
                  const isSelected = selectedIds.has(sessionId);
                  return (
                  <TableRow key={sessionId} className={isSelected ? "bg-blue-50" : ""}>
                    <TableCell>
                      <input 
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(sessionId)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {sessionId?.slice(0, 8)}...
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(session.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Hash className="w-3 h-3 text-slate-400" />
                        {session.message_count ?? (session.messages?.length || 0)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(session)}
                        {/* Voice & Resume indicators */}
                        {/* Check has_voice_message field OR look for voice message in preview */}
                        {(session.has_voice_message || 
                          session.voice_transcript || 
                          session.voice_storage_path ||
                          (session.last_message_preview && session.last_message_preview.toLowerCase().includes('sprachnachricht'))) && (
                          <Mic className="w-3.5 h-3.5 text-purple-500" title="Sprachnachricht vorhanden" />
                        )}
                        {session.lead?.resume_files && session.lead.resume_files.length > 0 && (
                          <FileText className="w-3.5 h-3.5 text-amber-500" title="Bewerbungsunterlagen vorhanden" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-slate-500">
                      {session.last_message_preview || "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewSession(sessionId)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-red-500 hover:text-red-600"
                          onClick={() => confirmDelete({...session, session_id: sessionId})}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-slate-600">
            Seite {page} von {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Chat-Verlauf
            </DialogTitle>
            {((selectedSession?.session?.transcript_json?.length > 0) || (selectedSession?.session?.messages?.length > 0)) && (
              <Button 
                variant="outline" 
                size="sm" 
                className="rounded-lg"
                onClick={copyTranscriptToClipboard}
                data-testid="copy-transcript-btn"
              >
                {copiedTranscript ? (
                  <>
                    <Check className="w-4 h-4 mr-2 text-green-500" />
                    Kopiert!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-2" />
                    Chat kopieren
                  </>
                )}
              </Button>
            )}
          </DialogHeader>
          
          {selectedSession && (
            <div className="space-y-4">
              {/* Session Info */}
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  {formatDate(selectedSession.session?.created_at)}
                </div>
                {selectedSession.lead && (
                  <Badge className="bg-green-100 text-green-700">
                    Lead: {selectedSession.lead.first_name}
                  </Badge>
                )}
              </div>
              
              {/* Voice Message Player */}
              {voiceMessage && voiceMessage.storage_path && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-4">
                    <audio 
                      id="chat-voice-audio-player"
                      src={`${API}/voice/audio/${voiceMessage.storage_path}`}
                      onEnded={() => setIsPlayingVoice(false)}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      className="w-12 h-12 rounded-full border-purple-300 hover:bg-purple-100"
                      onClick={() => {
                        const audio = document.getElementById('chat-voice-audio-player');
                        if (isPlayingVoice) {
                          audio.pause();
                          setIsPlayingVoice(false);
                        } else {
                          audio.play();
                          setIsPlayingVoice(true);
                        }
                      }}
                      data-testid="play-chat-voice-btn"
                    >
                      {isPlayingVoice ? (
                        <Pause className="w-6 h-6 text-purple-600" />
                      ) : (
                        <Play className="w-6 h-6 text-purple-600 ml-1" />
                      )}
                    </Button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Mic className="w-4 h-4 text-purple-500" />
                        <span className="font-medium text-purple-700">Sprachnachricht</span>
                      </div>
                      {voiceMessage.transcript && (
                        <p className="text-sm text-slate-600 mt-1 italic">"{voiceMessage.transcript}"</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Messages - Support both transcript_json and messages formats */}
              <div className="space-y-3 mt-4">
                {(selectedSession.session?.transcript_json || selectedSession.session?.messages || []).map((msg, idx) => (
                  <div 
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {msg.role !== 'user' && (
                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-blue-600" />
                      </div>
                    )}
                    <div 
                      className={`max-w-[80%] p-3 rounded-lg text-sm ${
                        msg.role === 'user' 
                          ? 'bg-blue-600 text-white rounded-br-sm' 
                          : 'bg-slate-100 text-slate-700 rounded-bl-sm'
                      }`}
                    >
                      {msg.content}
                    </div>
                    {msg.role === 'user' && (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-slate-600" />
                      </div>
                    )}
                  </div>
                ))}
                
                {(!(selectedSession.session?.transcript_json?.length) && !(selectedSession.session?.messages?.length)) && (
                  <p className="text-center text-slate-400 py-8">Keine Nachrichten vorhanden</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Single Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Chat-Session löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Chat-Session wird unwiderruflich gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={deleteSession}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              {bulkDeleteCount === "all" ? "Alle Chat-Sessions löschen?" : `${bulkDeleteCount} Chat-Sessions löschen?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {bulkDeleteCount === "all" 
                ? "ALLE Chat-Sessions werden unwiderruflich gelöscht!"
                : `Die ältesten ${bulkDeleteCount} Chat-Sessions werden unwiderruflich gelöscht.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={bulkDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={isDeleting}
            >
              {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Löschen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminChatSessions;
